import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';

const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * ファン好みの型定義
 */
export interface UsersPreferences {
  user_id: string;
  preferred_tone?: string;
  preferred_romantic_distance?: string;
  reassurance_style?: string;
  response_style_preference?: string;
  favorite_topics?: string[];
  created_at: string;
  updated_at: string;
}

/**
 * Supabaseからファン好みを管理するカスタムフック
 */
export function useSupabasePreferences(userId: string) {
  const [preferences, setPreferences] = useState<UsersPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isValidUserId = Boolean(userId && userId.trim() !== '');

  /**
   * 好みを取得
   */
  const fetchPreferences = useCallback(async () => {
    if (!isValidUserId) return null;
    try {
      const { data, error: fetchError } = await supabase
        .from('users_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setPreferences(data);
      return data;
    } catch (err) {
      console.error('好みの取得に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return null;
    }
  }, [userId, isValidUserId]);

  /**
   * 好みを更新
   */
  const updatePreferences = useCallback(async (
    updates: Partial<Omit<UsersPreferences, 'user_id' | 'created_at' | 'updated_at'>>
  ): Promise<boolean> => {
    if (!isValidUserId) return false;
    try {
      const { error: updateError } = await supabase
        .from('users_preferences')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId);

      if (updateError) throw updateError;

      await fetchPreferences();
      return true;
    } catch (err) {
      console.error('好みの更新に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return false;
    }
  }, [userId, isValidUserId, fetchPreferences]);

  /**
   * 好みを作成
   */
  const createPreferences = useCallback(async (
    data: Omit<UsersPreferences, 'created_at' | 'updated_at'>
  ): Promise<boolean> => {
    if (!isValidUserId) return false;
    try {
      const { error: insertError } = await supabase
        .from('users_preferences')
        .insert({
          ...data,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (insertError) throw insertError;

      await fetchPreferences();
      return true;
    } catch (err) {
      console.error('好みの作成に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return false;
    }
  }, [isValidUserId, fetchPreferences]);

  /**
   * 初回読み込み
   */
  useEffect(() => {
    const loadData = async () => {
      if (!isValidUserId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      await fetchPreferences();
      setIsLoading(false);
    };

    loadData();
  }, [fetchPreferences, isValidUserId]);

  return {
    preferences,
    isLoading,
    error,
    fetchPreferences,
    updatePreferences,
    createPreferences,
  };
}